import { DocumentReference } from 'firebase/firestore';
import { auth } from '../google/firebase';
import { EntityLabel, ExternalCompany, Scope } from '@esg/esg-global-types';
import { apollo_client } from './db_util';
import { EXTERNAL_COMPANIES_QUERY } from '../graphql/queries/external_company';
import {
  ADD_EXTERNAL_COMPANY,
  DELETE_EXTERNAL_COMPANY,
  UPDATE_EXTERNAL_COMPANY
} from '../graphql/mutations/external_company';

// Singular and plural label for model entity.
export const external_company_label: EntityLabel = {
  one: 'External Company',
  many: 'External Companies'
};

/**
 * Function to query all External Company docs for a Company in firestore.
 * @param {string} group_id id of Group to query External Companies for
 * @param {string} company_id id of Company to query External Companies for
 * @returns {Array<ExternalCompany>}
 */
export const getExternalCompanies = async (group_id: string, company_id: string) => {
  try {
    const external_companies: Array<ExternalCompany> = await apollo_client
      .query({
        query: EXTERNAL_COMPANIES_QUERY,
        variables: {
          group_id: group_id,
          company_id: company_id
        },
        fetchPolicy: 'no-cache'
      })
      .then((response) => response.data.external_companies);
    return external_companies;
  } catch (err) {
    const error = `Error while getting External Companies from Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : '',
      variables: {
        group_id: group_id,
        company_id: company_id
      }
    })}`;
    throw new Error(`Error: getExternalCompanies: ${JSON.stringify(error)}.`);
  }
};

/**
 * Create new External Company database record.
 * @param {string} group_id ID of Group to delete external company for
 * @param {string} company_id ID of Company to delete emission factor for
 * @param {any} external_company Object to be created
 * @returns {Promise<DocumentReference>}
 */
export const createExternalCompany = async (
  group_id: string,
  company_id: string,
  external_company: {
    name: string;
    scope: Scope;
  }
): Promise<DocumentReference> => {
  try {
    const external_company_doc = await apollo_client
      .mutate({
        mutation: ADD_EXTERNAL_COMPANY,
        variables: {
          group_id: group_id,
          company_id: company_id,
          external_company: external_company,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => response.data.createExternalCompany);
    return external_company_doc;
  } catch (err) {
    const error = `Error while creating External Company on Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: createExternalCompany: ${JSON.stringify(error)}.`);
  }
};

/**
 * Soft delete External Company.
 * @param {string} group_id ID of Group to delete external company for
 * @param {string} company_id ID of Company to delete emission factor for
 * @param {ExternalCompany} external_company Object to be deleted
 * @returns {Promise<void>}
 */
export const deleteExternalCompany = async (
  group_id: string,
  company_id: string,
  external_company: ExternalCompany
): Promise<void> => {
  try {
    await apollo_client.mutate({
      mutation: DELETE_EXTERNAL_COMPANY,
      variables: {
        group_id: group_id,
        company_id: company_id,
        external_company: external_company,
        user_email: auth.currentUser?.email
      }
    });
  } catch (err) {
    const error = `Error while deleting Emission Factor from Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: deleteEmissionFactor: ${JSON.stringify(error)}.`);
  }
};

/**
 * Update existing External Company database record.
 * @param {string} group_id ID of Group to delete external company for
 * @param {string} company_id ID of Company to delete emission factor for
 * @param {ExternalCompany} external_company Object to be updated.
 * @returns {Promise<void>}
 */
export const updateExternalCompany = async (
  group_id: string,
  company_id: string,
  external_company: ExternalCompany,
  original_company_data: ExternalCompany
): Promise<DocumentReference> => {
  try {
    const external_company_doc = await apollo_client
      .mutate({
        mutation: UPDATE_EXTERNAL_COMPANY,
        variables: {
          group_id: group_id,
          company_id: company_id,
          external_company: external_company,
          original_company_data: original_company_data
        }
      })
      .then((response) => response.data.updateExternalCompany);
    return external_company_doc;
  } catch (err) {
    const error = `Error while updating External Company from Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: updateExternalCompany: ${JSON.stringify(error)}.`);
  }
};
